import { useState, useEffect } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import dayjs from 'dayjs';
import axios from 'axios';

interface ObservationModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (observation: {
    text: string;
    local?: string;
    date: string;
    hour: string;
    id?: number;
  }) => void;
  matricula: string;
  guidCarro: string;
  tripId: number;
}

const apiClient = axios.create({
  baseURL: 'https://wsdigital.ctcp.pt/gcViagems',
  // baseURL: 'http://localhost:3400/gcViagems',
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const ObservationModal = ({
  show,
  onHide,
  onSuccess,
  matricula,
  guidCarro,
  tripId
}: ObservationModalProps) => {
  // State for form data
  const [observationText, setObservationText] = useState('');
  const [local, setLocal] = useState('');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [hour, setHour] = useState(dayjs().format('HH:mm'));

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setObservationText('');
      setLocal('');
      setDate(dayjs().format('YYYY-MM-DD'));
      setHour(dayjs().format('HH:mm'));
      setError(null);
    }
  }, [show]);

  const handleSubmit = async () => {
    if (!observationText.trim()) {
      setError('Please enter an observation text');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const datetime = dayjs(`${date} ${hour}`, 'YYYY-MM-DD HH:mm').format(
        'YYYY-MM-DD HH:mm:ss'
      );

      const payload = {
        guidCarro: guidCarro,
        observacao: observationText,
        data: datetime,
        idviagem: tripId,
        ...(local.trim() ? { local: local.trim() } : {})
      };

      const response = await apiClient.post('/markingObservations', payload);

      const observationData = {
        text: observationText,
        ...(local.trim() ? { local: local.trim() } : {}),
        date: date,
        hour: hour,
        id: response.data.id
      };

      onSuccess(observationData);
      onHide();
    } catch (err) {
      console.error('Failed to create observation:', err);
      setError('Failed to create observation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHide = () => {
    setObservationText('');
    setLocal('');
    setDate(dayjs().format('YYYY-MM-DD'));
    setHour(dayjs().format('HH:mm'));
    setError(null);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      contentClassName="border-translucent"
      size="lg"
    >
      <Modal.Header className="px-card border-0">
        <div className="w-100 d-flex justify-content-between align-items-start">
          <div>
            <h5 className="mb-0 lh-sm text-body-highlight">
              Create Observation
            </h5>
            {matricula && (
              <p className="mb-0 fs-10 text-body-tertiary">
                Carro: {matricula}
              </p>
            )}
          </div>
          <Button className="p-1 fs-10 text-body" onClick={handleHide}>
            X
          </Button>
        </div>
      </Modal.Header>

      <Form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Modal.Body className="p-card">
          {error && <div className="alert alert-danger mb-3 p-2">{error}</div>}

          {/* Observation Text */}
          <Form.Group className="mb-3">
            <Form.Label>Observation</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={observationText}
              onChange={e => setObservationText(e.target.value)}
              placeholder="Enter observation details..."
              required
            />
          </Form.Group>

          {/* Local */}
          <Form.Group className="mb-3">
            <Form.Label>Local (optional)</Form.Label>
            <Form.Control
              type="text"
              value={local}
              onChange={e => setLocal(e.target.value)}
              placeholder="Enter location..."
            />
          </Form.Group>

          {/* Date and Hour */}
          <div className="d-flex gap-3 mb-3">
            <Form.Group className="flex-grow-1">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="flex-grow-1">
              <Form.Label>Hour</Form.Label>
              <Form.Control
                type="time"
                value={hour}
                onChange={e => setHour(e.target.value)}
                required
              />
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end align-items-center border-0">
          <Button
            variant="primary"
            type="submit"
            disabled={isLoading || !observationText.trim()}
            className="px-4"
          >
            {isLoading ? (
              <Spinner
                animation="border"
                role="status"
                variant="light"
                size="sm"
              />
            ) : (
              'Create Observation'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ObservationModal;
